/**
 * Invisible notifier that watches the global "latest users · live=Y" feed
 * (the same data backing the Recently Active rail) and fires a toast in the
 * top-right whenever a NEW broadcaster goes live since the last poll.
 *
 * Why a separate component instead of hooking into `LastLoggedUsers`?
 *   - The rail is only mounted on dashboard pages that show the right rail.
 *   - We want the notification to fire across ALL authenticated dashboard
 *     routes (timeline, profile, friends, places, …), so this lives in
 *     `DashboardLayout` and reuses the cached query.
 *
 * Behaviour:
 *   - First poll (after mount): silently records the currently-live IDs;
 *     no toast — we don't want to spam users with "everyone who happens
 *     to be live right now" when they navigate to the dashboard.
 *   - Subsequent polls: diff against the seen set; for every NEW id, fire
 *     a toast with avatar + name + a primary CTA that routes to
 *     `/user/<u>/livestream`.
 *   - The viewer themselves are skipped (no self-notifications).
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Radio } from 'lucide-react';

import { Button, UserAvatar } from '@/components/ui';
import { useToast } from '@/components/ui/use-toast';
import { useUserList } from '@/lib/api';
import { useAuth } from '@/pages/auth';
import { fullName, initials } from '@/pages/profile/lib/profile';
import type { User } from '@/types/api';

/** Poll cadence — matches `LastLoggedUsers` so the cache is shared. */
const REFETCH_MS = 30_000;
const STALE_MS = 15_000;

export function LiveNotifier() {
  const me = useAuth((s) => s.user);
  const { toast } = useToast();

  // Reuse the same query the Recently Active rail uses so we don't burn an
  // extra request — TanStack dedupes by queryKey.
  const { data } = useUserList(
    { type: 'latest', limit: 10, live: 'Y' },
    { refetchInterval: REFETCH_MS, staleTime: STALE_MS, enabled: !!me?.ID },
  );

  // Track IDs already announced. First poll just seeds this set without
  // toasting; subsequent polls toast only for IDs not in the set.
  const seenRef = useRef<Set<number> | null>(null);

  useEffect(() => {
    if (!me?.ID || !data?.list) return;
    const liveNow = data.list.filter(
      (u) => u.live === true && u.ID !== me.ID && !!u.username,
    );
    const ids = new Set(liveNow.map((u) => u.ID));

    // First-ever observation — seed without toasting.
    if (seenRef.current === null) {
      seenRef.current = ids;
      return;
    }

    const previously = seenRef.current;
    const newcomers = liveNow.filter((u) => !previously.has(u.ID));
    seenRef.current = ids;

    for (const u of newcomers) {
      const name = fullName(u) || u.username || 'Someone';
      toast({
        duration: 8000,
        title: (
          <span className="flex items-center gap-2 text-sm">
            <Radio className="h-4 w-4 text-red-500" aria-hidden="true" />
            <span className="font-semibold">{name} is live</span>
          </span>
        ),
        description: <LivePreview user={u} />,
        action: (
          <Button
            asChild
            size="sm"
            className="rounded-full bg-red-500 px-3 hover:bg-red-600"
          >
            <Link to={`/user/${u.username}/livestream`}>Watch</Link>
          </Button>
        ),
      });
    }
  }, [data?.list, me?.ID, toast]);

  return null;
}

/** Avatar + name + "Tap to watch" inside the toast description slot. */
function LivePreview({ user }: { user: User }) {
  const name = fullName(user) || user.username || 'Member';
  return (
    <Link
      to={user.username ? `/user/${user.username}/livestream` : '#'}
      className="mt-1 flex items-center gap-2 text-xs text-muted-foreground hover:text-ink"
    >
      <UserAvatar
        src={user.image}
        alt={name}
        fallback={initials(user)}
        plan={user.plan && user.plan !== 'free' ? user.plan : undefined}
        className="h-8 w-8 outline outline-2 outline-red-500 outline-offset-2"
      />
      <span className="min-w-0 flex-1">
        {user.username ? <span className="block truncate">@{user.username}</span> : null}
        <span className="block text-[11px] text-muted-foreground/80">Tap to watch</span>
      </span>
    </Link>
  );
}
